import type { LayerSummary, PartSummary } from "../worker/splitWorker.ts";

/**
 * The side panel: the parts list, the layer list and the warnings.
 *
 * Plain DOM. There are two lists and a handful of buttons here, and a framework
 * would be more code than the thing it rendered.
 */

export interface PanelCallbacks {
  readonly onToggle: (index: number, additive: boolean) => void;
  readonly onHover: (index: number | null) => void;
  readonly onZoomTo: (index: number) => void;
  readonly onSelectAll: () => void;
  readonly onSelectNone: () => void;
  readonly onEditPartData: () => void;
  readonly onZoomToPoint: (x: number, y: number) => void;
  readonly onLayerRoleChange: (layer: string, role: string) => void;
  readonly onLayerVisibilityChange: (layer: string, hidden: boolean) => void;
  readonly onSetUnits: (code: number) => void;
  readonly onToleranceChange: (value: number) => void;
  readonly onToleranceReset: () => void;
  readonly onRemoveBorderChange: (value: boolean) => void;
  /** A line of raw DXF text was clicked in the Contents tab. 1-based. */
  readonly onContentsLineClick: (line: number) => void;
}

/** `$INSUNITS` codes for the Settings tab's two buttons. */
const IMPERIAL_UNITS_CODE = 1; // Inches
const METRIC_UNITS_CODE = 4; // Millimeters

/**
 * Every `$INSUNITS` code that is an imperial unit. Everything else with a
 * label (i.e. not 0/"Unitless") is shown as metric — the DXF unit table is
 * otherwise entirely SI, down to nanometers.
 */
const IMPERIAL_UNITS_CODES = new Set([1, 2, 3, 8, 9, 10]);

const ROLES = ["cut", "mark", "annotation", "ignore"] as const;

export class Panels {
  private readonly partsList: HTMLUListElement;
  private readonly layersList: HTMLUListElement;
  private readonly warnings: HTMLElement;
  private readonly count: HTMLElement;
  private readonly selectedCount: HTMLElement;
  private readonly panel: HTMLElement;
  private readonly layersLocked: HTMLElement;
  private readonly unitsStatus: HTMLElement;
  private readonly unitsActions: HTMLElement;
  private readonly toleranceInput: HTMLInputElement;
  private readonly toleranceReset: HTMLButtonElement;
  private readonly removeBorderToggle: HTMLInputElement;
  private readonly contentsText: HTMLPreElement;
  private readonly contentsSearchInput: HTMLInputElement;
  private readonly contentsSearchCount: HTMLElement;

  /** Rows by part index, so selection updates touch one node, not the list. */
  private readonly rows = new Map<number, HTMLLIElement>();
  private parts: readonly PartSummary[] = [];
  /** What the tolerance input showed before its last rejected entry, to restore on an invalid one. */
  private lastToleranceDisplay = "";
  /**
   * The loaded file's raw text, split into lines. Cached once per file so
   * highlighting never re-splits it, and so its numbering matches exactly
   * what `sliceLines()` (engine-side, used for export) already produces.
   */
  private lines: string[] = [];
  /** Which Contents sub-tab is showing. */
  private activeSubtab: "header" | "content" = "content";
  /** The current file's `HEADER` section, or `null` if it has none. */
  private headerRange: { start: number; end: number } | null = null;
  /** The last spans/variant passed to `setContentsHighlight`, re-applied on every render. */
  private highlightSpans: readonly { srcStart: number; srcEnd: number }[] = [];
  private highlightVariant: "part" | "entity" = "part";
  /** Character offsets into the active sub-tab's text. Empty means no search is active. */
  private searchMatches: { start: number; end: number }[] = [];
  private searchIndex = -1;

  constructor(
    private readonly root: Document,
    private readonly callbacks: PanelCallbacks,
  ) {
    this.panel = must(root, "panel");
    this.partsList = must(root, "parts") as HTMLUListElement;
    this.layersList = must(root, "layers") as HTMLUListElement;
    this.warnings = must(root, "warnings");
    this.count = must(root, "panel-count");
    this.selectedCount = must(root, "selected-count");
    this.layersLocked = must(root, "layers-locked");
    this.unitsStatus = must(root, "units-status");
    this.unitsActions = must(root, "units-actions");
    this.toleranceInput = must(root, "tolerance-input") as HTMLInputElement;
    this.toleranceReset = must(root, "tolerance-reset") as HTMLButtonElement;
    this.removeBorderToggle = must(root, "remove-border-toggle") as HTMLInputElement;
    this.contentsText = must(root, "contents-text") as HTMLPreElement;
    this.contentsSearchInput = must(root, "contents-search-input") as HTMLInputElement;
    this.contentsSearchCount = must(root, "contents-search-count");

    must(root, "select-all").addEventListener("click", () => this.callbacks.onSelectAll());
    must(root, "select-none").addEventListener("click", () => this.callbacks.onSelectNone());
    must(root, "edit-part-data").addEventListener("click", () => this.callbacks.onEditPartData());

    for (const tab of root.querySelectorAll<HTMLButtonElement>(".tab")) {
      tab.addEventListener("click", () => this.showTab(tab.dataset.tab ?? "parts"));
    }

    for (const subtab of root.querySelectorAll<HTMLButtonElement>("#tab-contents .subtab")) {
      subtab.addEventListener("click", () => {
        this.showContentsSubtab((subtab.dataset.subtab ?? "content") as "header" | "content");
      });
    }

    // Typing invalidates the last search (so the next Enter searches fresh);
    // it does not search live, matching the DXF Viewer's own search box.
    this.contentsSearchInput.addEventListener("input", () => {
      this.clearSearchState();
      this.renderContentsPane();
    });

    this.contentsSearchInput.addEventListener("keydown", (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      this.advanceSearch(event.shiftKey);
    });

    must(root, "units-imperial").addEventListener("click", () => this.callbacks.onSetUnits(IMPERIAL_UNITS_CODE));
    must(root, "units-metric").addEventListener("click", () => this.callbacks.onSetUnits(METRIC_UNITS_CODE));

    // `change`, not `input`: a value is only validated once the user commits it
    // (blur or Enter), not on every keystroke of a number they are still typing.
    this.toleranceInput.addEventListener("change", () => {
      const raw = this.toleranceInput.value.trim();
      if (raw === "") return; // empty does not implicitly revert to Auto — that's the reset button's job
      const value = Number(raw);
      if (!Number.isFinite(value) || value <= 0) {
        this.toleranceInput.value = this.lastToleranceDisplay;
        return;
      }
      this.callbacks.onToleranceChange(value);
    });
    this.toleranceReset.addEventListener("click", () => this.callbacks.onToleranceReset());

    this.removeBorderToggle.addEventListener("change", () =>
      this.callbacks.onRemoveBorderChange(this.removeBorderToggle.checked),
    );

    // One listener for the whole list rather than one per row: a 114-part nest
    // would otherwise attach several hundred.
    this.partsList.addEventListener("click", (event) => {
      const row = (event.target as HTMLElement).closest<HTMLLIElement>(".part");
      if (row === null) return;
      const index = Number(row.dataset.index);
      if ((event.target as HTMLElement).closest(".part__zoom") !== null) {
        this.callbacks.onZoomTo(index);
        return;
      }
      this.callbacks.onToggle(index, event.shiftKey || event.metaKey || event.ctrlKey);
    });

    this.partsList.addEventListener("mouseover", (event) => {
      const row = (event.target as HTMLElement).closest<HTMLLIElement>(".part");
      this.callbacks.onHover(row === null ? null : Number(row.dataset.index));
    });
    this.partsList.addEventListener("mouseleave", () => this.callbacks.onHover(null));

    this.contentsText.addEventListener("click", (event) => {
      const line = this.lineNumberAtClick(event);
      if (line !== null) this.callbacks.onContentsLineClick(line);
    });
  }

  show(): void {
    this.panel.hidden = false;
  }

  hide(): void {
    this.panel.hidden = true;
  }

  private showTab(name: string): void {
    for (const tab of this.root.querySelectorAll<HTMLButtonElement>(".tab")) {
      tab.classList.toggle("tab--on", tab.dataset.tab === name);
    }
    must(this.root, "tab-parts").hidden = name !== "parts";
    must(this.root, "tab-layers").hidden = name !== "layers";
    must(this.root, "tab-contents").hidden = name !== "contents";
    must(this.root, "tab-settings").hidden = name !== "settings";
  }

  private activateSubtab(name: "header" | "content"): void {
    for (const btn of this.root.querySelectorAll<HTMLButtonElement>("#tab-contents .subtab")) {
      btn.classList.toggle("subtab--on", btn.dataset.subtab === name);
    }
    this.activeSubtab = name;
  }

  private showContentsSubtab(name: "header" | "content"): void {
    this.activateSubtab(name);
    this.clearSearchState();
    this.contentsText.scrollTop = 0;
    this.renderContentsPane();
  }

  renderParts(parts: readonly PartSummary[]): void {
    this.parts = parts;
    this.rows.clear();
    this.partsList.replaceChildren();

    const fragment = this.root.createDocumentFragment();
    for (const part of parts) {
      const row = this.root.createElement("li");
      row.className = "part";
      row.dataset.index = String(part.index);

      const num = this.root.createElement("span");
      num.className = "part__num";
      num.textContent = String(part.index + 1);

      const body = this.root.createElement("span");
      const name = this.root.createElement("span");
      name.className = "part__name";
      name.textContent = part.name;
      name.title = part.name;

      const meta = this.root.createElement("span");
      meta.className = "part__meta";
      meta.textContent = describe(part);

      body.append(name, meta);

      const zoom = this.root.createElement("button");
      zoom.className = "iconbtn part__zoom";
      zoom.type = "button";
      zoom.innerHTML =
        '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">' +
        '<circle cx="6.5" cy="6.5" r="4.5" fill="none" stroke="currentColor" stroke-width="1.6"/>' +
        '<line x1="9.8" y1="9.8" x2="14" y2="14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
        "</svg>";
      zoom.title = "Zoom to this part";
      zoom.setAttribute("aria-label", "Zoom to this part");

      row.append(num, body, zoom);

      // The two cases worth flagging in the list, because both mean the name
      // is not the part's own: a collision, and a fallback index.
      if (part.nameSource === "index" || part.deduplicated) {
        const flag = this.root.createElement("span");
        flag.className = "part__flag";
        flag.textContent = part.nameSource === "index" ? "unnamed" : "duplicate name";
        meta.append(" · ", flag);
      }

      this.rows.set(part.index, row);
      fragment.append(row);
    }

    this.partsList.append(fragment);
    this.count.textContent = parts.length === 1 ? "1 part" : `${parts.length} parts`;
  }

  renderSelection(selected: ReadonlySet<number>): void {
    for (const [index, row] of this.rows) {
      row.classList.toggle("is-selected", selected.has(index));
    }
    const n = selected.size;
    this.selectedCount.textContent =
      n === 0 ? "none selected" : n === this.parts.length ? "all selected" : `${n} selected`;
  }

  renderLayers(
    layers: readonly LayerSummary[],
    editable: boolean,
    overrides: ReadonlyMap<string, string> = new Map(),
    hidden: ReadonlySet<string> = new Set(),
  ): void {
    this.layersList.replaceChildren();
    this.layersLocked.hidden = editable;

    // A nest routinely declares layers it never draws on — this corpus has one
    // file declaring nineteen and using two. They are worth showing, because a
    // user looking for a layer that "should be there" needs to see it exists,
    // but not worth seventeen rows above the two that matter.
    const used = layers.filter((l) => l.counts.curve + l.counts.label + l.counts.other > 0);
    const empty = layers.filter((l) => l.counts.curve + l.counts.label + l.counts.other === 0);

    const fragment = this.root.createDocumentFragment();
    for (const layer of used) {
      fragment.append(this.layerRow(layer, editable, overrides.get(layer.layer), hidden.has(layer.layer)));
    }

    if (empty.length > 0) {
      const item = this.root.createElement("li");
      item.className = "layer layer--empty";

      const details = this.root.createElement("details");

      const summary = this.root.createElement("summary");
      summary.className = "layer__summary";
      summary.textContent =
        empty.length === 1
          ? "1 declared layer with nothing drawn on it"
          : `${empty.length} declared layers with nothing drawn on them`;

      const names = this.root.createElement("p");
      names.className = "layer__why";
      names.textContent = empty.map((l) => (l.layer === "" ? "(unnamed)" : l.layer)).join(", ");

      details.append(summary, names);
      item.append(details);
      fragment.append(item);
    }

    this.layersList.append(fragment);
  }

  /**
   * One layer's row.
   *
   * The role shown is the role of what the layer actually holds. Showing the
   * curve role unconditionally was wrong and read as a bug: a text-only layer
   * has no curves, so its curve role is `ignore`, and the panel announced
   * "ignore" directly above a sentence explaining it was an annotation layer.
   */
  private layerRow(
    layer: LayerSummary,
    editable: boolean,
    override: string | undefined,
    hidden: boolean,
  ): HTMLLIElement {
    const item = this.root.createElement("li");
    item.className = "layer";

    const top = this.root.createElement("div");
    top.className = "layer__top";

    const visibility = this.root.createElement("input");
    visibility.type = "checkbox";
    visibility.className = "layer__visibility";
    visibility.checked = !hidden;
    visibility.title = hidden ? "Show this layer" : "Hide this layer";
    visibility.addEventListener("change", () => {
      this.callbacks.onLayerVisibilityChange(layer.layer, !visibility.checked);
    });

    const name = this.root.createElement("span");
    name.className = "layer__name";
    name.textContent = layer.layer === "" ? "(unnamed)" : layer.layer;
    name.title = layer.layer;

    const counts = this.root.createElement("span");
    counts.className = "layer__counts";
    counts.textContent = summariseCounts(layer);

    top.append(visibility, name, counts);

    const roles = this.root.createElement("span");
    roles.className = "layer__role";

    // A layer holding both geometry and text gets both roles, because they
    // genuinely differ — `zmeny` puts 50 part labels and 14 scribe lines on
    // layer 0, and one badge cannot describe that honestly.
    if (layer.counts.curve > 0) {
      if (editable) {
        // The dropdown must show what the user actually picked, not the
        // original guess — otherwise it silently snaps back to "Cut" after an
        // override, looking like the change had no effect.
        roles.append(this.roleSelect(layer, override ?? layer.roles.curve));
      } else {
        roles.append(this.roleBadge(layer.roles.curve));
      }
    }
    if (layer.counts.label > 0) {
      roles.append(this.roleBadge(layer.roles.label));
    }

    top.append(roles);

    const why = this.root.createElement("p");
    why.className = "layer__why";
    why.textContent = layer.reason;

    item.append(top, why);
    return item;
  }

  private roleBadge(role: string): HTMLSpanElement {
    const badge = this.root.createElement("span");
    badge.className = `role role--${role}`;
    badge.textContent = role;
    return badge;
  }

  private roleSelect(layer: LayerSummary, current: string): HTMLSelectElement {
    const select = this.root.createElement("select");
    for (const role of ROLES) {
      const option = this.root.createElement("option");
      option.value = role;
      option.textContent = role;
      option.selected = current === role;
      select.append(option);
    }
    select.addEventListener("change", () => {
      this.callbacks.onLayerRoleChange(layer.layer, select.value);
    });
    return select;
  }

  renderUnits(units: { code: number | null; label: string | null }): void {
    if (units.label !== null && units.code !== null) {
      this.unitsStatus.textContent = IMPERIAL_UNITS_CODES.has(units.code) ? "Imperial" : "Metric";
      this.unitsActions.hidden = true;
    } else {
      this.unitsStatus.textContent = "Not specified";
      this.unitsActions.hidden = false;
    }
  }

  /**
   * @param resolvedJoin The join tolerance this split actually used — the
   *   Auto placeholder when no override is set, otherwise the same as `override`.
   * @param override The user's fixed value, or `null` for Auto.
   */
  renderTolerance(resolvedJoin: number, override: number | null): void {
    if (override === null) {
      this.toleranceInput.value = "";
      this.toleranceInput.placeholder = `Auto (${formatTolerance(resolvedJoin)})`;
      this.toleranceReset.hidden = true;
      this.lastToleranceDisplay = "";
    } else {
      const display = String(override);
      this.toleranceInput.value = display;
      this.toleranceReset.hidden = false;
      this.lastToleranceDisplay = display;
    }
  }

  renderRemoveBorder(value: boolean): void {
    this.removeBorderToggle.checked = value;
  }

  /** Load a file's raw text into the Contents tab, with no highlight yet. */
  renderContents(source: string): void {
    this.lines = source.split(/\r\n|\r|\n/);
    this.headerRange = findHeaderRange(this.lines);
    this.highlightSpans = [];
    this.contentsSearchInput.value = "";
    this.activateSubtab("content");
    this.clearSearchState();
    this.contentsText.scrollTop = 0;
    this.renderContentsPane();
  }

  /**
   * Highlight the given 1-based, inclusive line ranges in the Contents tab.
   * Rebuilds the text as a small number of plain-text/`<mark>` segments —
   * never one DOM node per line — so cost scales with the number of
   * highlighted regions, not the file's length. A non-empty highlight always
   * switches to the Content sub-tab (there's nothing to show it on Header)
   * and drops any active search, since only one of the two can be drawn.
   */
  setContentsHighlight(
    spans: readonly { srcStart: number; srcEnd: number }[],
    opts: { scrollIntoView: boolean },
    variant: "part" | "entity" = "part",
  ): void {
    this.highlightSpans = spans;
    this.highlightVariant = variant;
    if (spans.length > 0) {
      if (this.activeSubtab !== "content") this.activateSubtab("content");
      this.clearSearchState();
    }
    const { firstMark } = this.renderContentsPane();
    if (opts.scrollIntoView && firstMark) firstMark.scrollIntoView({ block: "center" });
  }

  /** 1-based, inclusive — the same convention the engine's own `sliceLines()` uses for export. */
  private joinLines(a: number, b: number): string {
    return this.lines.slice(a - 1, b).join("\n");
  }

  /** The active sub-tab's lines, or `null` for Header when the file has no `HEADER` section. */
  private activeSubtabLines(): string[] | null {
    if (this.activeSubtab === "content") return this.lines;
    if (this.headerRange === null) return null;
    return this.lines.slice(this.headerRange.start - 1, this.headerRange.end);
  }

  /**
   * The one place that writes to `contentsText`. Draws at most one of a
   * search-match highlight or an entity/part-focus highlight (search wins,
   * since it's the one the user is actively driving), falling back to plain
   * text for whichever sub-tab is active.
   */
  private renderContentsPane(): { firstMark: HTMLElement | null; currentSearchMark: HTMLElement | null } {
    const lines = this.activeSubtabLines();
    if (lines === null) {
      this.contentsText.textContent = "No HEADER section found in this file.";
      return { firstMark: null, currentSearchMark: null };
    }

    if (this.searchMatches.length > 0) {
      return this.renderSearchMarks(lines);
    }

    if (this.activeSubtab === "content" && this.highlightSpans.length > 0) {
      const firstMark = this.renderLineHighlight(this.highlightSpans, this.highlightVariant);
      return { firstMark, currentSearchMark: null };
    }

    this.contentsText.textContent = lines.join("\n");
    return { firstMark: null, currentSearchMark: null };
  }

  /** Entity/part-focus highlight, by 1-based absolute line spans (Content sub-tab only). */
  private renderLineHighlight(
    spans: readonly { srcStart: number; srcEnd: number }[],
    variant: "part" | "entity",
  ): HTMLElement | null {
    const merged = mergeSpans(spans);
    const fragment = this.root.createDocumentFragment();
    let cursor = 1;
    let firstMark: HTMLElement | null = null;

    for (const { srcStart, srcEnd } of merged) {
      if (srcStart > cursor) {
        fragment.append(this.root.createTextNode(this.joinLines(cursor, srcStart - 1) + "\n"));
      }
      const mark = this.root.createElement("mark");
      if (variant === "entity") mark.className = "contents-text__entity-focus";
      mark.textContent = this.joinLines(srcStart, srcEnd) + "\n";
      fragment.append(mark);
      if (firstMark === null) firstMark = mark;
      cursor = srcEnd + 1;
    }
    if (cursor <= this.lines.length) {
      fragment.append(this.root.createTextNode(this.joinLines(cursor, this.lines.length)));
    }

    this.contentsText.replaceChildren(fragment);
    return firstMark;
  }

  /** Search-match highlight, by character offsets into the active sub-tab's joined text. */
  private renderSearchMarks(lines: readonly string[]): {
    firstMark: HTMLElement | null;
    currentSearchMark: HTMLElement | null;
  } {
    const text = lines.join("\n");
    const fragment = this.root.createDocumentFragment();
    let cursor = 0;
    let firstMark: HTMLElement | null = null;
    let currentSearchMark: HTMLElement | null = null;

    this.searchMatches.forEach((match, i) => {
      if (match.start > cursor) {
        fragment.append(this.root.createTextNode(text.slice(cursor, match.start)));
      }
      const mark = this.root.createElement("mark");
      mark.className = "contents-text__search-match";
      if (i === this.searchIndex) {
        mark.classList.add("contents-text__search-match--current");
        currentSearchMark = mark;
      }
      mark.textContent = text.slice(match.start, match.end);
      fragment.append(mark);
      if (firstMark === null) firstMark = mark;
      cursor = match.end;
    });
    if (cursor < text.length) {
      fragment.append(this.root.createTextNode(text.slice(cursor)));
    }

    this.contentsText.replaceChildren(fragment);
    return { firstMark, currentSearchMark };
  }

  /** Drop any computed search results — typing, switching sub-tabs, and loading a file all do this. */
  private clearSearchState(): void {
    this.searchMatches = [];
    this.searchIndex = -1;
    this.contentsSearchCount.textContent = "";
  }

  /**
   * Enter/Shift+Enter in the search box. Computes matches against the active
   * sub-tab's text on the first press for a given term (typing already
   * cleared any earlier matches), then just cycles — with wraparound — on
   * every press after that, exactly like the DXF Viewer's search box.
   */
  private advanceSearch(backwards: boolean): void {
    const term = this.contentsSearchInput.value.trim();
    if (term === "") return;

    if (this.searchMatches.length === 0) {
      const lines = this.activeSubtabLines();
      if (lines !== null) this.searchMatches = findMatches(lines.join("\n"), term);
    }
    if (this.searchMatches.length === 0) {
      this.contentsSearchCount.textContent = "No results";
      return;
    }

    this.searchIndex = backwards
      ? this.searchIndex <= 0
        ? this.searchMatches.length - 1
        : this.searchIndex - 1
      : (this.searchIndex + 1) % this.searchMatches.length;

    this.contentsSearchCount.textContent = `${this.searchIndex + 1} of ${this.searchMatches.length}`;
    const { currentSearchMark } = this.renderContentsPane();
    currentSearchMark?.scrollIntoView({ block: "center" });
  }

  /**
   * Which raw-text line a click landed on, given the segment-based rendering
   * `renderContentsPane` builds (a handful of plain-text/`<mark>` top-level
   * children, never one per line). Counts newlines in every top-level sibling
   * before the clicked node, plus newlines up to the click offset within it.
   * On the Header sub-tab (a slice of the full file), the result is offset
   * back to an absolute line number.
   */
  private lineNumberAtClick(event: MouseEvent): number | null {
    const pos = caretPositionAt(event.clientX, event.clientY);
    if (pos === null || !this.contentsText.contains(pos.node)) return null;

    let newlines = 0;
    const topLevel = pos.node.parentElement === this.contentsText ? pos.node : pos.node.parentElement;
    if (topLevel === null) return null;
    for (let sib = topLevel.previousSibling; sib; sib = sib.previousSibling) {
      newlines += (sib.textContent?.match(/\n/g) ?? []).length;
    }
    const within = pos.node.textContent?.slice(0, pos.offset) ?? "";
    newlines += (within.match(/\n/g) ?? []).length;

    const relativeLine = newlines + 1;
    if (this.activeSubtab === "header" && this.headerRange !== null) {
      return relativeLine + this.headerRange.start - 1;
    }
    return relativeLine;
  }

  renderWarnings(
    warnings: readonly { code: string; severity: string; message: string; at?: { x: number; y: number } }[],
  ): void {
    this.warnings.replaceChildren();

    const fragment = this.root.createDocumentFragment();
    for (const warning of warnings) {
      const item = this.root.createElement("div");
      item.className = `warning warning--${warning.severity}`;

      const text = this.root.createElement("span");
      text.textContent = warning.message;
      item.append(text);

      // A warning about a place in the drawing is far more useful with a way
      // to go and look at it.
      if (warning.at !== undefined) {
        const at = warning.at;
        const zoom = this.root.createElement("button");
        zoom.className = "linkbtn warning__zoom";
        zoom.type = "button";
        zoom.textContent = "show";
        zoom.addEventListener("click", () => this.callbacks.onZoomToPoint(at.x, at.y));
        item.append(zoom);
      }

      fragment.append(item);
    }

    this.warnings.append(fragment);
  }
}

function describe(part: PartSummary): string {
  const size = `${round(part.width)} × ${round(part.height)}`;
  const bits = [size];
  if (part.holeCount > 0) bits.push(`${part.holeCount} ${part.holeCount === 1 ? "hole" : "holes"}`);
  if (part.markCount > 0) bits.push(`${part.markCount} marks`);
  return bits.join(" · ");
}

function summariseCounts(layer: LayerSummary): string {
  const bits: string[] = [];
  if (layer.counts.curve > 0) bits.push(`${layer.counts.curve} geometry`);
  if (layer.counts.label > 0) bits.push(`${layer.counts.label} text`);
  if (bits.length === 0) return "empty";
  return bits.join(", ");
}

function round(n: number): string {
  if (n >= 100) return n.toFixed(0);
  if (n >= 10) return n.toFixed(1);
  return n.toFixed(2);
}

/**
 * Format a join tolerance for the Auto placeholder. These routinely sit in the
 * 1e-3 to 1e-9 range (per `tolerance.ts`'s own worked examples), where a
 * couple of decimal places would collapse most real values to "0" — hence the
 * exponential fallback for anything below 0.001, mirroring the shape of the
 * engine's own `formatNumber` (not reusable here: it isn't exported across the
 * worker boundary).
 */
function formatTolerance(n: number): string {
  if (n === 0) return "0";
  if (Math.abs(n) < 0.001) return n.toExponential(2);
  return n.toFixed(7).replace(/0+$/, "").replace(/\.$/, "");
}

/** Sort and merge touching/overlapping 1-based inclusive line ranges. */
function mergeSpans(
  spans: readonly { srcStart: number; srcEnd: number }[],
): { srcStart: number; srcEnd: number }[] {
  const sorted = [...spans].sort((a, b) => a.srcStart - b.srcStart);
  const merged: { srcStart: number; srcEnd: number }[] = [];
  for (const span of sorted) {
    const last = merged[merged.length - 1];
    if (last && span.srcStart <= last.srcEnd + 1) {
      last.srcEnd = Math.max(last.srcEnd, span.srcEnd);
    } else {
      merged.push({ ...span });
    }
  }
  return merged;
}

/**
 * The file's `HEADER` section as a 1-based, inclusive line range — covering
 * both the `0`/`SECTION`/`2`/`HEADER` marker lines and the closing `0`/`ENDSEC`
 * pair — or `null` if the file has no `HEADER` section. Pure string-array
 * scan over the lines the client already has; no engine involvement needed.
 */
function findHeaderRange(lines: readonly string[]): { start: number; end: number } | null {
  const trimmed = lines.map((line) => line.trim());

  let start = -1;
  for (let i = 0; i + 3 < trimmed.length; i++) {
    if (trimmed[i] === "0" && trimmed[i + 1] === "SECTION" && trimmed[i + 2] === "2" && trimmed[i + 3] === "HEADER") {
      start = i;
      break;
    }
  }
  if (start === -1) return null;

  for (let i = start + 4; i + 1 < trimmed.length; i++) {
    if (trimmed[i] === "0" && trimmed[i + 1] === "ENDSEC") {
      return { start: start + 1, end: i + 2 };
    }
  }
  return null;
}

/** Every case-insensitive, non-overlapping occurrence of `term` in `text`, as `[start, end)` offsets. */
function findMatches(text: string, term: string): { start: number; end: number }[] {
  const matches: { start: number; end: number }[] = [];
  const haystack = text.toLowerCase();
  const needle = term.toLowerCase();
  let from = 0;
  for (;;) {
    const at = haystack.indexOf(needle, from);
    if (at === -1) break;
    matches.push({ start: at, end: at + needle.length });
    from = at + needle.length;
  }
  return matches;
}

/**
 * Where a click landed, as a text node and a character offset into it.
 * Wraps the two browser APIs for this (Firefox/Chrome's `caretPositionFromPoint`,
 * Safari/older Chrome's `caretRangeFromPoint`) since neither is universal.
 */
function caretPositionAt(x: number, y: number): { node: Node; offset: number } | null {
  const withCaretPosition = document as Document & {
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
  };
  if (typeof withCaretPosition.caretPositionFromPoint === "function") {
    const pos = withCaretPosition.caretPositionFromPoint(x, y);
    if (pos === null) return null;
    return { node: pos.offsetNode, offset: pos.offset };
  }

  const withCaretRange = document as Document & {
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
  };
  if (typeof withCaretRange.caretRangeFromPoint === "function") {
    const range = withCaretRange.caretRangeFromPoint(x, y);
    if (range === null) return null;
    return { node: range.startContainer, offset: range.startOffset };
  }

  return null;
}

function must(root: Document, id: string): HTMLElement {
  const element = root.getElementById(id);
  if (element === null) throw new Error(`The page is missing #${id}.`);
  return element;
}
